// "Ask your boat to suggest alarms" (task alarms_regenerate_ui, ADR 0160).
//
// The generator runs ON THE BOAT — only the boat can see its own devices — so
// this page cannot compute anything. It asks, waits for the boat's proposal to
// come back over the relay, shows it, and sends the operator's ticks back for
// the boat to write. The cloud never writes an alarm definition on this path;
// the boat's own up-sync is what makes the Alarms page catch up.
//
// Five things on this screen are non-negotiable, and all five exist because the
// failure mode here is a confident-looking wrong answer rather than an error:
//
//   1. PROVENANCE IS ABOVE THE LIST, ALWAYS. A proposal built from zero
//      harvested device settings is a vessel-class DEFAULT set. It renders
//      identically to a tuned one — same recipes, same tidy list — so the page
//      says which it is, in the operator's words, before they read a single
//      suggestion. `describeProvenance` owns that judgement.
//   2. NEW IS TICKED, EXISTING IS NOT. Adding an alarm the boat lacks is the
//      point of the feature. Rewriting the numbers on one that already exists
//      touches something the operator may have tuned by hand, so it is never a
//      default.
//   3. A TICK THAT WOULD DO NOTHING IS NOT OFFERED. An apply writes thresholds
//      only, so an alarm that drifted on its name or severity alone cannot be
//      reconciled. Those rows are labelled, not given a dead checkbox.
//   4. AN ANSWER CARRIES ITS AGE. A stored proposal lives 24 h and the page
//      adopts whatever is there on open. With no date, yesterday's answer — and
//      device settings the boat read two years ago — read as measurements of
//      the boat as it is now.
//   5. WRITTEN IS NOT ARMED. The boat compiles what it wrote against its live
//      signal catalog; a definition it cannot bind is saved and watching
//      nothing. `describeApplied` never merges the two claims.
//
// A failure NEVER destroys the proposal. Offline, timeout and error are notes
// ABOVE the list, each with a way forward — losing a screen of considered ticks
// to a dropped link is its own defect.
//
// House rule observed: no coloured left-edge accent stripes anywhere here —
// the panels use a uniform border plus a background fill.

import { demoLimitMessage } from '../lib/demo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { useBoatChrome } from '../components/app/boatChrome';
import { ChevronRightIcon, InfoCircleIcon } from '../components/app/icons';
import { alarmSeverityLabel, type AlarmSeverity } from '../lib/alarmDisplay';
import { findTemplate, useAlertTemplates } from '../lib/alertTemplates';
import {
  alreadyCoveredNote,
  buildApplyBody,
  isAlreadyCovered,
  isRegenerationNotStoredError,
  regenerationNotStoredDetail,
  changedKeyLabel,
  conditionLine,
  defaultSelection,
  describeApplied,
  describeProvenance,
  groupItems,
  hasPlan,
  isTickable,
  isUpgrade,
  upgradeNote,
  parsePendingRequest,
  pendingRequestKey,
  proposalAgeLine,
  refusalLabel,
  skipReasonLabel,
  type RegenerateMode,
  type RegenerationItem,
  type RegenerationPayload,
  type StoredRegeneration,
} from '../lib/alarmRegenerate';
import { isBoatOfflineRelayError } from '../lib/revealStaging';
import { useDocumentTitle } from '../lib/useDocumentTitle';

/** Poll cadence + ceiling while waiting for the boat's reply to land. */
const POLL_MS = 1_500;
const POLL_TIMEOUT_MS = 60_000;

type RegenerateAction = 'preview' | 'apply';

interface RelayResponse {
  requestId: string;
}

/**
 * What the page is DOING. Deliberately separate from the proposal itself, which
 * lives in its own state and survives every one of these: a 409 or a dropped
 * request must not take a screen of ticks with it.
 *
 * Every failure phase carries the action it failed on, because "nothing was
 * changed" is only true of a preview. An apply the cloud staged for delivery
 * may well have run on the boat by the time the browser gives up waiting.
 */
type Phase =
  | { kind: 'loading' }
  | { kind: 'idle' }
  | { kind: 'asking'; action: RegenerateAction }
  | { kind: 'ready' }
  | { kind: 'offline'; action: RegenerateAction }
  | { kind: 'timeout'; action: RegenerateAction }
  // The boat answered and the CLOUD could not keep it. Distinct from `timeout`
  // because the advice is opposite: waiting and retrying cannot help.
  | { kind: 'not_stored'; action: RegenerateAction; detail?: string }
  | { kind: 'error'; action: RegenerateAction; message: string };

/**
 * The request this tab is still waiting on for this boat, or `null`.
 *
 * `sessionStorage` rather than component state because the whole point is to
 * survive a navigation away and back: the page tells the operator the answer
 * will be here when they return, and without this the remount would read the
 * newest STORED proposal — the previous one — and render it as the answer to
 * the request they just made.
 */
function readPending(boatId: string) {
  try {
    return parsePendingRequest(sessionStorage.getItem(pendingRequestKey(boatId)), POLL_TIMEOUT_MS);
  } catch {
    return null;
  }
}

function markPending(boatId: string, requestId: string, action: RegenerateAction): void {
  try {
    sessionStorage.setItem(
      pendingRequestKey(boatId),
      JSON.stringify({ requestId, action, startedAt: Date.now() })
    );
  } catch {
    // A browser refusing storage costs the resume, nothing else.
  }
}

function clearPending(boatId: string): void {
  try {
    sessionStorage.removeItem(pendingRequestKey(boatId));
  } catch {
    // Same.
  }
}

export function AlarmsRegenerate() {
  const { boatId, boat, alerts } = useBoatChrome();
  const refreshAlerts = alerts.refresh;
  useDocumentTitle(boat ? `${boat.display_name} — Suggest alarms` : 'Suggest alarms — Neomaris');

  const [mode, setMode] = useState<RegenerateMode>('hardware');
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [proposal, setProposal] = useState<RegenerationPayload | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Cancels an in-flight poll loop when the component unmounts or a newer
  // request supersedes it — without this a stale loop can overwrite the phase
  // set by a newer one.
  const pollToken = useRef(0);

  const backToAlarms = `/dashboard/boats/${boatId}/alarms`;

  const adopt = useCallback((payload: RegenerationPayload) => {
    setProposal(payload);
    setMode(payload.mode);
    setSelected(defaultSelection(payload.items));
    setPhase({ kind: 'ready' });
  }, []);

  /**
   * Poll for the boat's answer to ONE request id. The boat computes locally and
   * ships the proposal up its outbox, so the round trip is seconds on a live
   * boat — but it rides the same queue as everything else, so a ceiling and an
   * honest timeout beat an indefinite spinner.
   *
   * `deadline` is absolute so a wait RESUMED after a navigation keeps the
   * original one rather than starting a fresh minute.
   */
  const waitFor = useCallback(
    async (requestId: string, action: RegenerateAction, deadline: number): Promise<void> => {
      const token = (pollToken.current += 1);
      while (pollToken.current === token && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
        if (pollToken.current !== token) return;
        try {
          const stored = await api<StoredRegeneration>(
            `/v1/boats/${boatId}/alarms/regenerate/preview?requestId=${encodeURIComponent(requestId)}`
          );
          if (pollToken.current !== token) return;
          clearPending(boatId);
          adopt(stored.payload);
          // An apply changed what the boat has armed; re-read the shared
          // snapshot so the Alarms page behind this one is not stale.
          if (action === 'apply' && stored.payload.status === 'applied') refreshAlerts();
          return;
        } catch (err) {
          // A 404 is the ordinary "not landed yet" — keep waiting. But a 503
          // `regeneration_not_stored` means the boat DID answer and this server
          // could not keep it, so waiting is pointless and saying "your boat has
          // not answered" would blame the wrong end of the system.
          if (isRegenerationNotStoredError(err)) {
            if (pollToken.current !== token) return;
            clearPending(boatId);
            const detail = regenerationNotStoredDetail(err);
            setPhase({ kind: 'not_stored', action, ...(detail ? { detail } : {}) });
            return;
          }
        }
      }
      if (pollToken.current !== token) return;
      clearPending(boatId);
      setPhase({ kind: 'timeout', action });
    },
    [boatId, adopt, refreshAlerts]
  );

  // On open: resume a wait this tab left in flight, else show whatever the boat
  // last sent. A proposal survives a reload, and making the operator re-ask (and
  // re-wait) for something already stored would be pointless. A 404 is the
  // honest "never asked" empty state.
  useEffect(() => {
    let live = true;
    const pending = readPending(boatId);
    void (async () => {
      try {
        const stored = await api<StoredRegeneration>(
          `/v1/boats/${boatId}/alarms/regenerate/preview`
        );
        if (!live) return;
        // A pending request whose answer has NOT arrived must not be answered
        // with the previous proposal — that is the stale-answer bug this marker
        // exists to close. Only adopt the stored row when nothing is in flight
        // or when the stored row IS the awaited answer.
        if (pending === null || stored.payload.requestId === pending.requestId) {
          clearPending(boatId);
          adopt(stored.payload);
          return;
        }
      } catch {
        // 404 (never asked) and any transient read failure fall through.
      }
      if (!live) return;
      if (pending === null) {
        setPhase({ kind: 'idle' });
        return;
      }
      setPhase({ kind: 'asking', action: pending.action });
      await waitFor(pending.requestId, pending.action, pending.startedAt + POLL_TIMEOUT_MS);
    })();
    return () => {
      live = false;
    };
  }, [boatId, adopt, waitFor]);

  useEffect(() => () => void (pollToken.current += 1), []);

  async function send(action: RegenerateAction, body: object): Promise<void> {
    setPhase({ kind: 'asking', action });
    try {
      const res = await api<RelayResponse>(`/v1/boats/${boatId}/alarms/regenerate`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      });
      markPending(boatId, res.requestId, action);
      await waitFor(res.requestId, action, Date.now() + POLL_TIMEOUT_MS);
    } catch (err) {
      // The proposal on screen is untouched on purpose: a dropped link is not a
      // reason to throw away the operator's selection. A Try it guest past its
      // cap gets the server's own words (TOM-868).
      const e = err as { status?: number; body?: unknown };
      const limit = demoLimitMessage(e.status ?? 0, e.body);
      setPhase(
        limit !== null
          ? { kind: 'error', action, message: limit }
          : isBoatOfflineRelayError(err)
            ? { kind: 'offline', action }
            : {
                kind: 'error',
                action,
                message: 'We couldn’t reach your boat just now. Please try again.',
              }
      );
    }
  }

  async function ask(nextMode: RegenerateMode): Promise<void> {
    setMode(nextMode);
    await send('preview', { action: 'preview', mode: nextMode });
  }

  async function apply(payload: RegenerationPayload): Promise<void> {
    await send('apply', buildApplyBody(payload.items, selected, payload.mode, payload.storeDigest));
  }

  function toggle(recipeId: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  }

  const busy = phase.kind === 'asking';
  const retry = (
    <button
      type="button"
      className="btn btn-secondary btn-compact"
      data-testid="regen-retry"
      disabled={busy}
      onClick={() => void ask(mode)}
    >
      Ask again
    </button>
  );

  return (
    <section data-testid="alarms-regenerate">
      <Link className="detail-back" to={backToAlarms}>
        ← Back to alarms
      </Link>

      <div className="page-head">
        <h1>Suggest alarms for {boat?.display_name ?? 'this boat'}</h1>
        <p className="page-sub">
          Your boat works out what is worth watching from the equipment it can see, and tells us.
          Nothing changes until you choose.
        </p>
      </div>

      {/* Said once, plainly, before anything else — including before the boat
          has been asked. "Regenerate" sounds like it could replace things.

          THIS COPY USED TO SAY "Nothing is removed", and as of the upgrade lane
          (core ADR-116 / ADR 0177) that is no longer true: a row offering to
          replace an alarm you already have retires it when you tick it. Leaving
          the old sentence up would have been the worst option available —
          shipping a removal under a promise that denies removals exist.

          The no-surprise guarantee is not weakened, it is made precise: the
          removal is named on the row that causes it, it needs a tick, and the
          tick is off by default. That is a stronger claim than the vague one it
          replaces, and it is one the page can actually keep. */}
      <p className="regen-promise" data-testid="regen-promise">
        This <strong>adds</strong> alarms. Nothing is switched off, and anything you leave unticked
        is left exactly as it is. One kind of row can replace an alarm you already have — it says so
        on the row, its numbers carry over, and it is <strong>unticked</strong> unless you tick it.
      </p>

      {phase.kind === 'loading' ? <p className="muted">Checking with your boat…</p> : null}

      {phase.kind === 'asking' ? (
        <div className="regen-waiting" role="status" data-testid="regen-waiting">
          <p>
            {phase.action === 'apply'
              ? 'Asking your boat to set these up…'
              : 'Asking your boat what it would suggest…'}
          </p>
          <p className="muted">
            It works this out itself, so this takes a few seconds. You can leave this page — the
            answer will be here when you come back.
          </p>
        </div>
      ) : null}

      {phase.kind === 'offline' ? (
        <div className="regen-note regen-note--warn" role="alert" data-testid="regen-offline">
          <InfoCircleIcon />
          <div>
            <p>Your boat is offline right now.</p>
            <p className="muted">
              It has to work the suggestions out itself, so this has to wait until it is back
              online. Nothing was changed
              {phase.action === 'apply' ? ' — your choices below are still here' : ''}.
            </p>
            {retry}
          </div>
        </div>
      ) : null}

      {phase.kind === 'not_stored' ? (
        <div className="regen-note regen-note--warn" role="alert" data-testid="regen-not-stored">
          <InfoCircleIcon />
          <div>
            <p>Your boat answered, but we could not save its reply.</p>
            {/* Deliberately NOT "your boat is slow". This is a fault on our side,
                and the operator must not be sent to check the boat — or worse,
                to keep retrying something that cannot succeed until we fix it.
                The boat keeps its answer queued, so nothing is lost. */}
            <p className="muted">
              {phase.action === 'apply'
                ? 'This is a problem on our side, not with your boat. Your boat still has its answer and will send it again once we can store it, so nothing is lost — but we cannot yet tell you whether these were set up. Check your alarms in a moment.'
                : 'This is a problem on our side, not with your boat — trying again will not help until it is fixed. Your boat still has its answer and will send it again once we can store it. Nothing on your boat was changed.'}
            </p>
            {phase.detail !== undefined ? (
              <p className="muted regen-note__detail" data-testid="regen-not-stored-detail">
                {phase.detail}
              </p>
            ) : null}
            {phase.action === 'apply' ? (
              <Link className="btn btn-secondary btn-compact" to={backToAlarms}>
                See your alarms
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {phase.kind === 'timeout' ? (
        <div className="regen-note regen-note--warn" role="alert" data-testid="regen-timeout">
          <InfoCircleIcon />
          <div>
            <p>Your boat has not answered yet.</p>
            {/* An apply that timed out is NOT "nothing happened". The command
                may have been staged and delivered when the boat reconnected, in
                which case the alarms exist. Saying otherwise would send the
                operator away believing their boat is unwatched. */}
            <p className="muted">
              {phase.action === 'apply'
                ? 'It may be busy or on a slow link, so we cannot yet tell whether it set these up. Check your alarms in a moment — anything it did set up will appear there.'
                : 'It may be busy or on a slow link. Nothing was changed — try again, or come back to this page later.'}
            </p>
            {phase.action === 'apply' ? (
              <Link className="btn btn-secondary btn-compact" to={backToAlarms}>
                See your alarms
              </Link>
            ) : (
              retry
            )}
          </div>
        </div>
      ) : null}

      {phase.kind === 'error' ? (
        <div className="regen-note regen-note--warn" role="alert" data-testid="regen-error">
          <InfoCircleIcon />
          <div>
            <p>{phase.message}</p>
            <p className="muted">
              Nothing was changed
              {phase.action === 'apply' ? ' — your choices below are still here' : ''}.
            </p>
            {retry}
          </div>
        </div>
      ) : null}

      {/* The mode picker is the only "ask" control, so it stays available until
          there IS something to look at. Once a proposal is on screen the same
          two choices live in its toolbar. */}
      {proposal === null && phase.kind !== 'loading' && phase.kind !== 'asking' ? (
        <ModePicker mode={mode} onMode={setMode} onAsk={() => void ask(mode)} busy={busy} />
      ) : null}

      {proposal !== null ? (
        <Proposal
          payload={proposal}
          selected={selected}
          busy={busy}
          onToggle={toggle}
          onApply={() => void apply(proposal)}
          onAsk={(m) => void ask(m)}
          backToAlarms={backToAlarms}
        />
      ) : null}
    </section>
  );
}

function ModePicker({
  mode,
  onMode,
  onAsk,
  busy,
}: {
  mode: RegenerateMode;
  onMode: (m: RegenerateMode) => void;
  onAsk: () => void;
  busy: boolean;
}) {
  return (
    <div className="regen-start" data-testid="regen-start">
      <fieldset className="regen-modes">
        <legend>What should your boat base this on?</legend>
        <label className={`regen-mode${mode === 'hardware' ? ' regen-mode--on' : ''}`}>
          <input
            type="radio"
            name="regen-mode"
            value="hardware"
            checked={mode === 'hardware'}
            data-testid="regen-mode-hardware"
            onChange={() => onMode('hardware')}
          />
          <span className="regen-mode__body">
            <span className="regen-mode__title">The equipment on board now</span>
            <span className="regen-mode__sub">
              Looks at everything your boat can currently see, including anything fitted since
              setup. This is usually what you want.
            </span>
          </span>
        </label>
        <label className={`regen-mode${mode === 'decisions' ? ' regen-mode--on' : ''}`}>
          <input
            type="radio"
            name="regen-mode"
            value="decisions"
            checked={mode === 'decisions'}
            data-testid="regen-mode-decisions"
            onChange={() => onMode('decisions')}
          />
          <span className="regen-mode__body">
            <span className="regen-mode__title">What you chose during setup</span>
            <span className="regen-mode__sub">
              Rebuilds the same set you picked the first time. Useful if alarms went missing and you
              want them back as they were.
            </span>
          </span>
        </label>
      </fieldset>
      <button
        type="button"
        className="btn btn-primary"
        disabled={busy}
        data-testid="regen-ask"
        onClick={onAsk}
      >
        {busy ? 'Asking…' : 'Ask my boat'}
      </button>
    </div>
  );
}

function Proposal({
  payload,
  selected,
  busy,
  onToggle,
  onApply,
  onAsk,
  backToAlarms,
}: {
  payload: RegenerationPayload;
  selected: ReadonlySet<string>;
  busy: boolean;
  onToggle: (recipeId: string) => void;
  onApply: () => void;
  onAsk: (mode: RegenerateMode) => void;
  backToAlarms: string;
}) {
  // A refusal the boat reached before it could build a plan carries honest
  // ZEROES for every provenance field, because the schema requires them and the
  // boat genuinely does not know. Rendering those would put "worked out from 0
  // devices" and "nothing to suggest" on screen over a look the boat never
  // took. Show the refusal alone.
  const planned = hasPlan(payload);
  const provenance = describeProvenance(payload);
  const groups = groupItems(payload.items);
  // The recipe LIBRARY, only to turn `alreadyCoveredBy`'s id into the name the
  // owner sees that alarm under. Module-cached and static per deploy, so this
  // costs at most one request per session — and the superseded recipes it has to
  // resolve are still in it, which is why they are projected rather than
  // filtered out server-side. `undefined` while it loads or if it fails, which
  // `alreadyCoveredNote` degrades to a true sentence without the name.
  const templatesState = useAlertTemplates();
  const titleOf = useCallback(
    (recipeId: string): string | undefined =>
      templatesState.kind === 'ready'
        ? findTemplate(templatesState.templates, recipeId)?.title
        : undefined,
    [templatesState]
  );
  // Whether the default-ticked promise the band's subtitle makes is actually
  // true of this list. One already-covered row makes it false, and a subtitle
  // that contradicts the checkboxes below it is worse than a longer sentence.
  // An UPGRADE row is unticked for its own, different reason, so the two are
  // counted apart — a subtitle saying "already covered by an alarm you have" over
  // a row that says "replaces the alarm you have" is the contradiction
  // `alreadyCoveredNote` suppresses, reappearing one level up.
  const anyUpgrade = groups.fresh.some(isUpgrade);
  const anyCovered = groups.fresh.some((i) => isAlreadyCovered(i) && !isUpgrade(i));
  const tickedCount = payload.items.filter((i) => selected.has(i.recipeId) && isTickable(i)).length;
  const applied = payload.status === 'applied' ? payload.outcome : undefined;
  const refused = payload.status === 'refused' ? payload.error : undefined;
  const outcome = applied === undefined ? undefined : describeApplied(applied, titleOf);
  const otherMode: RegenerateMode = payload.mode === 'hardware' ? 'decisions' : 'hardware';

  return (
    <>
      {refused !== undefined ? (
        <div className="regen-note regen-note--warn" role="alert" data-testid="regen-refused">
          <InfoCircleIcon />
          <div>
            <p>{refusalLabel(refused.code)}</p>
            <p className="muted">Nothing on your boat was changed.</p>
            {!planned ? (
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                data-testid="regen-refused-retry"
                disabled={busy}
                onClick={() => onAsk(payload.mode)}
              >
                Ask again
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {outcome !== undefined ? (
        <div
          className={`regen-note ${outcome.incomplete ? 'regen-note--warn' : 'regen-note--done'}`}
          role="status"
          data-testid="regen-applied"
        >
          {outcome.incomplete ? <InfoCircleIcon /> : null}
          <div>
            <p>{outcome.headline}</p>
            {/* WRITTEN IS NOT ARMED. Kept as its own sentence so it can say
                "saved but not watched" without the headline contradicting it. */}
            {outcome.arming !== null ? (
              <p className="muted" data-testid="regen-arming">
                {outcome.arming}
              </p>
            ) : null}
            {applied !== undefined && applied.skipped.length > 0 ? (
              <ul className="regen-skipped" data-testid="regen-skipped">
                {applied.skipped.map((s) => (
                  <li key={s.recipeId}>
                    <span className="regen-skipped__id">{s.recipeId}</span>{' '}
                    {skipReasonLabel(s.reason)}
                  </li>
                ))}
              </ul>
            ) : null}
            <Link className="btn btn-secondary btn-compact" to={backToAlarms}>
              See your alarms
            </Link>
          </div>
        </div>
      ) : null}

      {planned ? (
        <>
          {/* PROVENANCE, above the list. A thin answer must look thin. */}
          <section
            className={`regen-provenance regen-provenance--${provenance.quality}`}
            data-testid="regen-provenance"
          >
            <h2 className="regen-provenance__head">
              <span className={`regen-chip regen-chip--${provenance.quality}`}>
                {provenance.quality === 'tuned'
                  ? 'Based on your gear'
                  : provenance.quality === 'partial'
                    ? 'Partly generic'
                    : 'Standard numbers'}
              </span>
              {provenance.headline}
            </h2>
            <ul className="regen-provenance__notes">
              {provenance.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </section>

          <div className="regen-toolbar">
            <div className="regen-toolbar__text">
              <p className="regen-count" data-testid="regen-count">
                {groups.fresh.length === 0
                  ? 'Your boat has nothing new to suggest.'
                  : `${groups.fresh.length} suggestion${groups.fresh.length === 1 ? '' : 's'} your boat does not have yet.`}
              </p>
              {/* A stored proposal can be up to a day old, or someone else's.
                  Undated, it reads as a measurement taken just now. */}
              <p className="regen-age" data-testid="regen-age">
                {proposalAgeLine(payload)}{' '}
                {payload.mode === 'hardware'
                  ? 'From the equipment on board at the time.'
                  : 'From what you chose during setup.'}
              </p>
            </div>
            <div className="regen-toolbar__actions">
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                data-testid="regen-reask"
                disabled={busy}
                onClick={() => onAsk(payload.mode)}
              >
                Ask again
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                data-testid="regen-reask-other"
                disabled={busy}
                onClick={() => onAsk(otherMode)}
              >
                {payload.mode === 'hardware'
                  ? 'Try what I chose at setup'
                  : 'Try the equipment on board'}
              </button>
            </div>
          </div>

          {groups.fresh.length > 0 ? (
            <section className="regen-band" data-testid="regen-band-new">
              <h2 className="regen-band__title">Not set up yet</h2>
              <p className="regen-band__sub">{freshBandSubtitle(anyCovered, anyUpgrade)}</p>
              <ul className="regen-list">
                {groups.fresh.map((item) => (
                  <ItemRow
                    key={item.recipeId}
                    item={item}
                    checked={selected.has(item.recipeId)}
                    onToggle={onToggle}
                    coveredNote={alreadyCoveredNote(item, titleOf)}
                    upgradeNote={upgradeNote(item, titleOf)}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          {groups.differs.length > 0 ? (
            <section className="regen-band" data-testid="regen-band-differs">
              <h2 className="regen-band__title">Already set up, but with different numbers</h2>
              <p className="regen-band__sub">
                Left unticked on purpose — you may have set these yourself. Ticking one changes only
                its numbers; its name, how serious it is, and whether it is switched on stay as you
                have them.
              </p>
              <ul className="regen-list">
                {groups.differs.map((item) => (
                  <ItemRow
                    key={item.recipeId}
                    item={item}
                    checked={selected.has(item.recipeId)}
                    onToggle={onToggle}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          {groups.unchanged.length > 0 ? (
            <details
              className="section-collapse regen-unchanged"
              data-testid="regen-band-unchanged"
            >
              <summary>
                <ChevronRightIcon />
                {groups.unchanged.length} already exactly as your boat would suggest
              </summary>
              <ul className="regen-list regen-list--quiet">
                {groups.unchanged.map((item) => (
                  <li
                    key={item.recipeId}
                    className="regen-row regen-row--quiet"
                    data-testid={`regen-item-${item.recipeId}`}
                  >
                    <span className="regen-row__title">{item.title}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          {payload.items.length === 0 ? (
            <div className="empty-card" data-testid="regen-empty">
              <h3>Your boat had nothing to suggest.</h3>
              <p className="muted">
                It could not match any of its equipment to an alarm we know how to build. You can
                still add one yourself.
              </p>
              <Link className="btn btn-primary" to={`${backToAlarms}/new`}>
                Add an alarm
              </Link>
            </div>
          ) : null}

          {payload.items.length > 0 ? (
            <div className="regen-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || tickedCount === 0}
                data-testid="regen-apply"
                onClick={onApply}
              >
                {tickedCount === 0
                  ? 'Nothing ticked'
                  : `Set up ${tickedCount} alarm${tickedCount === 1 ? '' : 's'}`}
              </button>
              <Link className="btn btn-secondary" to={backToAlarms}>
                Cancel
              </Link>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/**
 * The subtitle over the "Not set up yet" band, which has to describe the
 * checkboxes UNDER it or it is a lie.
 *
 * Three unticked reasons can coexist in one band and each needs its own clause:
 * an ordinary addition is ticked, an already-covered row is not (you would end
 * up with two alarms on one tank), and an UPGRADE row is not (ticking it removes
 * something). The upgrade clause is stated even when it is the only one, because
 * it is the only sentence on the page — other than the row itself — that admits
 * a removal is on offer.
 */
function freshBandSubtitle(anyCovered: boolean, anyUpgrade: boolean): string {
  const base = 'Ticked by default — these are additions.';
  const covered =
    ' The ones left unticked are already covered by an alarm you have; tick them only if you want both.';
  const upgrade =
    ' A row that says it replaces one of your alarms is unticked too — ticking that one removes the alarm it names.';
  if (anyCovered && anyUpgrade) return `${base}${covered}${upgrade}`;
  if (anyUpgrade) return `${base}${upgrade}`;
  if (anyCovered) return `${base}${covered}`;
  return base;
}

function ItemRow({
  item,
  checked,
  onToggle,
  coveredNote,
  upgradeNote: upgrade,
}: {
  item: RegenerationItem;
  checked: boolean;
  onToggle: (recipeId: string) => void;
  /** "Already covered by your existing … alarm", or null. `new` band only. */
  coveredNote?: string | null;
  /**
   * "Replaces your existing … alarm — its numbers carry over", or null. Mutually
   * exclusive with {@link coveredNote} by construction: `alreadyCoveredNote`
   * returns null whenever this one does not, so the two can never both render.
   */
  upgradeNote?: string | null;
}) {
  const tickable = isTickable(item);
  const changed = (item.changedKeys ?? []).map(changedKeyLabel);
  const gate = conditionLine(item);
  const inputId = `regen-tick-${item.recipeId}`;

  return (
    <li
      className={`regen-row${tickable ? '' : ' regen-row--locked'}`}
      data-testid={`regen-item-${item.recipeId}`}
    >
      {tickable ? (
        <input
          id={inputId}
          type="checkbox"
          className="regen-row__tick"
          checked={checked}
          data-testid={`regen-tick-${item.recipeId}`}
          onChange={() => onToggle(item.recipeId)}
        />
      ) : (
        // No dead checkbox. An apply writes numbers only, so there is nothing
        // this row could do — say that instead of pretending.
        <span className="regen-row__tick regen-row__tick--none" aria-hidden="true" />
      )}
      <div className="regen-row__body">
        <label className="regen-row__title" htmlFor={tickable ? inputId : undefined}>
          {item.title}
          {/* The boat's severity is a plain string on the wire; the label
              function already falls back for anything unrecognised. */}
          <span className={`alarm-sev alarm-sev--${item.severity}`}>
            {alarmSeverityLabel(item.severity as AlarmSeverity)}
          </span>
        </label>
        {item.explanation ? <p className="regen-row__why">{item.explanation}</p> : null}
        {/* Why this one arrives unticked while its neighbours are ticked. Left
            OUT of the explanation prose deliberately: that is the boat's
            reasoning about the alarm, this is about the owner's existing
            alarms, and merging them would hide the second inside the first. */}
        {coveredNote != null ? (
          <p className="regen-row__covered" data-testid={`regen-covered-${item.recipeId}`}>
            {coveredNote}
          </p>
        ) : null}
        {/* The UPGRADE row's promise, and the only place on this page that names
            a removal before it happens. It stands where the overlap note would
            have gone, never beside it. */}
        {upgrade != null ? (
          <p className="regen-row__upgrade" data-testid={`regen-upgrade-${item.recipeId}`}>
            {upgrade}
          </p>
        ) : null}
        {item.status === 'differs' && changed.length > 0 ? (
          <p className="regen-row__diff" data-testid={`regen-diff-${item.recipeId}`}>
            Different from yours: {changed.join(', ')}.
          </p>
        ) : null}
        {!tickable ? (
          <p className="regen-row__locked" data-testid={`regen-locked-${item.recipeId}`}>
            Nothing to apply here — your boat only rewrites the numbers, and the numbers already
            match. The rest is yours to change.
          </p>
        ) : null}
        {item.proposedModes.length > 0 ? (
          <p className="regen-row__modes">Armed when: {item.proposedModes.join(', ')}</p>
        ) : null}
        {/* The gate a create writes, such as the engine-running condition on an
            engine alarm (TOM-309). Absent for an older boat, which sends none. */}
        {gate !== null ? (
          <p className="regen-row__condition" data-testid={`regen-condition-${item.recipeId}`}>
            {gate}
          </p>
        ) : null}
      </div>
    </li>
  );
}
